import { ApiNotificationsNotificationTypeChoices, NotificationsType } from "@api/graphql/types-and-hooks";

const getNotificationPayload = (notification: NotificationsType): Record<string, unknown> | null => {
  try {
    return typeof notification.payload === "string" ? JSON.parse(notification.payload) : notification.payload;
  } catch {
    return null;
  }
};

/** Internal paths only: rejects control chars (browsers strip tab/CR/LF, so "/\t/host" becomes "//host"), "//host", "/\\host" and absolute URLs. */
const isInternalPath = (path: string): boolean => {
  if (!path.startsWith("/") || /[\u0000-\u001f\u007f]/.test(path)) return false;
  try {
    return new URL(path, window.location.origin).origin === window.location.origin;
  } catch {
    return false;
  }
};

const asNonEmptyString = (value: unknown): string | undefined =>
  typeof value === "string" && value.length > 0 ? value : undefined;

/** Single source of truth for notification navigation URLs. Returns the URL for payload-based routing, or undefined. */
export const getNotificationNavigationUrl = (notification: NotificationsType): string | undefined => {
  const type = notification.notificationType.toLowerCase();
  const payload = getNotificationPayload(notification);
  if (!payload) return undefined;

  if (
    [
      ApiNotificationsNotificationTypeChoices.ExpertChatMatchAssigned.toLowerCase(),
      ApiNotificationsNotificationTypeChoices.ExpertChatReplyNeeded.toLowerCase(),
      ApiNotificationsNotificationTypeChoices.ExpertChatNewMessage.toLowerCase(),
    ].includes(type)
  ) {
    return `/frontera-clarity/expert-chat/${payload.chatId}`;
  }

  const reportId = asNonEmptyString(payload.reportId);
  if (type === ApiNotificationsNotificationTypeChoices.ProgrammingRefreshCompleted.toLowerCase() && reportId) {
    return `/clinical-reports/reassessment/${reportId}/select-programming`;
  }

  const reportPath = asNonEmptyString(payload.reportPath);
  if (
    type === ApiNotificationsNotificationTypeChoices.ReportReview.toLowerCase() &&
    reportPath &&
    isInternalPath(reportPath)
  ) {
    return reportPath;
  }

  return undefined;
};

/** Returns a visible CTA label for notifications that show an action link in the UI. */
export const getNotificationLink = (notification: NotificationsType): string | null => {
  if (
    notification.notificationType.toLowerCase() ===
      ApiNotificationsNotificationTypeChoices.ProgrammingRefreshCompleted.toLowerCase() &&
    getNotificationNavigationUrl(notification)
  ) {
    return "Go to reassessment";
  }
  if (
    notification.notificationType.toLowerCase() ===
      ApiNotificationsNotificationTypeChoices.ReportReview.toLowerCase() &&
    getNotificationNavigationUrl(notification)
  ) {
    return "Open report";
  }
  return null;
};
